import { DateTime } from 'luxon';
import {
	DataMapperMathFunctions,
	DataMapperTransformFunctions,
} from '../../data-mapper-transform-functions';
import { DataMapperMappingTypes } from '../../enums/data-mapper-mapping-types';
import { SubstringEndIndexOptions } from '../../enums/data-mapper/substring-end-index-options.enum';
import { SubstringStartIndexOptions } from '../../enums/data-mapper/substring-start-index-options.enum';
import { IfGateDateTimeFormatType } from '../../enums/if-gate/if-gate-date-time-format-type';
import { ReplaceTypes } from '../../enums/replace-types';
import { TransformDatetimeTypes } from '../../enums/transform-datetime-types.enum';
import { TransformFunctionType } from '../../enums/transform-function-types.enum';
import { IMappingField } from '../../interfaces/data-mapper/mapping-field';
import { TransformFunction } from '../../interfaces/data-mapper/transform-function';
import { DataMapperSourceParams } from '../../interfaces/data-mapper/transform-params/data-mapper-source-params';
import { FunctionParamBase } from '../../interfaces/data-mapper/transform-params/function-param-base';
import { TransformDataTypes } from '../../types/transform-data-types';

/**
 * Handles the execution and application of data transformation functions
 * used in the data mapper pipeline. Supports string, number, object, and
 * datetime transformations.
 */
export class TransformProcessor {
	/**
	 * Converts a raw value to a specific type, validating that the existing
	 * type (if known) matches the expected types.
	 *
	 * @param currentValue - The raw value to convert.
	 * @param type - The known type of the value, if available.
	 * @param checkType - An array of acceptable target types (e.g. ['string'], ['number']).
	 * @returns The value cast to the appropriate type.
	 * @throws If the known type does not match any of the acceptable types.
	 */
	returnTypedValue(
		currentValue: TransformDataTypes,
		type: string | undefined,
		checkType: string[]
	) {
		try {
			if (type && !checkType.includes(type)) {
				throw new Error(`Type of input ${currentValue} (${type}) is not of type ${checkType}`);
			}
			if (checkType.includes('number')) {
				if (typeof currentValue === 'string') {
					return Number(currentValue.replace(/['"]+/g, ''));
				} else {
					return Number(currentValue);
				}
			}
			if (checkType.includes('string')) {
				return currentValue as string;
			}
			if (checkType.includes('object')) {
				if (typeof currentValue === 'string') {
					return JSON.parse(currentValue as any);
				} else {
					return currentValue;
				}
			}
			if (checkType.includes('date')) {
				return new Date(currentValue as string);
			}
			return JSON.parse(JSON.stringify(currentValue));
		} catch (e) {
			throw new Error((e as Error).message);
		}
	}

	/**
	 * Resolves a value against the current source input list.
	 * If the value matches a source input ID, the corresponding source's
	 * current value is returned typed. Otherwise, the raw value is typed directly.
	 *
	 * @param value - A raw value or a source input ID to look up.
	 * @param type - An array of acceptable target types.
	 * @param sourceInput - The list of current source input parameters.
	 * @returns The resolved and typed value.
	 */
	checkValue(value: TransformDataTypes, type: string[], sourceInput: DataMapperSourceParams[]) {
		const temp = sourceInput.find(x => x.id === value);
		if (temp !== undefined) {
			return this.returnTypedValue(temp.currentValue, temp.type, type);
		} else {
			return this.returnTypedValue(value, undefined, type);
		}
	}

	/**
	 * Rounds a number up or down to a given power of 10.
	 * For example, rounding 1234 to power 2 gives 1300 (UP) or 1200 (DOWN).
	 *
	 * @param value - The number to round.
	 * @param power - The power of 10 to round to (e.g. 2 = nearest 100).
	 * @param direc - Direction to round: 'UP' uses Math.ceil, 'DOWN' uses Math.floor.
	 * @returns The rounded number.
	 */
	roundUpOrDown(value: number, power: number, direc: 'UP' | 'DOWN') {
		const factor = Math.pow(10, power);
		if (direc === 'UP') {
			return Math.ceil(value / factor) * factor;
		} else {
			return Math.floor(value / factor) * factor;
		}
	}

	/**
	 * Resolves and type-checks the input parameters of a transform function
	 * against the expected input types defined by that function.
	 * Handles up to three positional values (value, value2, value3).
	 *
	 * @param transformFunction - The transform function whose params need to be resolved.
	 * @param types - An array of type arrays, one per positional param.
	 * @param sourceInput - The list of current source input parameters.
	 * @returns The resolved input parameters with typed values.
	 */
	checkInput(
		transformFunction: TransformFunction,
		types: string[][],
		sourceInput: DataMapperSourceParams[]
	) {
		const inputValues = transformFunction.selectedParams;
		if (types.length > 0 && inputValues.value) {
			inputValues.value = this.checkValue(inputValues.value, types[0], sourceInput);
		}
		if (types.length > 1 && inputValues.value2) {
			inputValues.value2 = this.checkValue(inputValues.value2, types[1], sourceInput);
		}
		if (types.length > 2 && inputValues.value3) {
			inputValues.value3 = this.checkValue(inputValues.value3, types[2], sourceInput);
		}
		return inputValues;
	}

	/**
	 * Executes a sequence of transform functions against a set of source inputs,
	 * passing the output of each step as an updated source for the next.
	 *
	 * - Skips any transform step where a required source value is missing.
	 * - Supports array inputs by applying the transform to each element individually.
	 * - Handles special combine and math functions by merging their inputs into a new
	 *   derived source entry.
	 *
	 * @param transformFunctions - Ordered list of transform functions to execute.
	 * @param sourceInput - The initial source input parameters.
	 * @param destinationMappingField - Optional destination field metadata, used to
	 *   determine whether array outputs should be preserved or collapsed to a single value.
	 * @returns The updated list of source input parameters after all transforms have run,
	 *   or `undefined` if processing is interrupted.
	 * @throws If a transform function has no input binding or is unsupported.
	 */
	run(
		transformFunctions: TransformFunction[],
		sourceInput: DataMapperSourceParams[],
		destinationMappingField?: IMappingField
	): DataMapperSourceParams[] | undefined {
		if (transformFunctions.length === 0) return sourceInput;
		let tempSourceInput: DataMapperSourceParams[] = JSON.parse(JSON.stringify(sourceInput));
		let outputValues:
			| {
					key: string;
					type: string;
					value: any;
			  }
			| undefined;

		for (const transformFunction of transformFunctions) {
			if (!transformFunction.selectedParams.value)
				throw new Error('An input parameter binding must be provided.');

			let shouldContinue = false;
			const tempTransformFunction = JSON.parse(JSON.stringify(transformFunction));

			for (const sourceInput of tempSourceInput) {
				if (!sourceInput.currentValue) {
					shouldContinue = true;
					console.log(`The property ${sourceInput.id} is not in the input payload.`);
					break;
				}
			}
			if (shouldContinue) continue;

			const dataMapperTransform = DataMapperTransformFunctions.find(
				x => x.id === transformFunction.type
			);
			if (!dataMapperTransform?.inputTypes) {
				throw new Error('This function is not supported');
			}

			const inputValue = this.checkInput(
				tempTransformFunction,
				dataMapperTransform.inputTypes,
				tempSourceInput
			);

			if (Array.isArray(inputValue.value)) {
				const results: any[] = [];
				let dataType: any;

				for (const value of inputValue.value) {
					const tempInputValue = {
						...inputValue,
						value: value,
					};

					const result = this.applyTransformation(transformFunction, tempInputValue, outputValues);

					results.push(result?.value);
					dataType = result?.type;
				}

				if (destinationMappingField && destinationMappingField.isArray) {
					outputValues = {
						key: `${transformFunction.type} ${transformFunction.index + 1} Output`,
						type: dataType,
						value: results,
					};
				} else {
					outputValues = {
						key: `${transformFunction.type} ${transformFunction.index + 1} Output`,
						type: dataType,
						value: results[0],
					};
				}
			} else {
				outputValues = this.applyTransformation(transformFunction, inputValue, outputValues);
			}

			for (const temp of tempSourceInput) {
				if (temp.id === transformFunction.selectedParams.value) {
					if (
						outputValues?.value !== undefined &&
						outputValues.value !== '' &&
						transformFunction.type !== TransformFunctionType.Combine
					) {
						if (
							(DataMapperTransformFunctions.filter(
								x => x.dataType === 'number' && x.type === transformFunction.type
							).length > 0 &&
								!DataMapperMathFunctions.includes(transformFunction.type)) ||
							DataMapperTransformFunctions.filter(
								x => x.dataType === 'number' && x.type === transformFunction.type
							).length === 0
						) {
							temp.previousValue = temp.currentValue;
							temp.currentValue = outputValues.value;
							temp.type = outputValues.type;
						}
					}
				}
			}

			if (
				outputValues?.value &&
				(transformFunction.type === TransformFunctionType.Combine ||
					DataMapperMathFunctions.includes(transformFunction.type))
			) {
				const id1 = tempSourceInput.find(x => x.id === transformFunction.selectedParams.value);
				const id2 = tempSourceInput.find(x => x.id === transformFunction.selectedParams.value2);

				if (id1 !== undefined) {
					let newId;
					if (id2 === undefined) {
						newId = `${id1.id}-CustomInput`;
					} else {
						newId = `${id1.id}-${id2.id}`;
					}

					const otherSource: DataMapperSourceParams[] = [];
					tempSourceInput.forEach(source => {
						if (source.id !== id1.id && source.id !== id2?.id && source.id !== newId) {
							otherSource.push(source);
						}
					});

					if (outputValues !== undefined) {
						otherSource.push({
							id: newId,
							type: outputValues?.type,
							currentValue: outputValues.value,
							previousValue: '',
							startIndex: transformFunction.index + 1,
							endIndex: undefined,
						});
					}
					tempSourceInput = otherSource;
				}
			}
		}
		return tempSourceInput;
	}

	/**
	 * Applies a single transformation to the given input value and returns
	 * the result as a typed output object.
	 *
	 * Supported transformations include:
	 * - **String**: Trim, TrimStart, TrimEnd, ToUpperCase, ToLowerCase, AddPrefix, AddSuffix,
	 *   Combine, Substring, Replace, ObjectToString, NumToString, StringToNum, DatetimeToString, StringToObject
	 * - **Math**: Add, Subtract, Multiply, Divide, RoundUp, RoundDown, MathCeil, MathFloor
	 * - **Object**: AddField, RemoveField
	 * - **DateTime**: StringToDatetime, AddTime
	 *
	 * @param transformFunction - The transform function definition, including its type and index.
	 * @param inputValue - The resolved input parameters to transform.
	 * @param outputValues - The previous step's output, passed through if no transformation matches.
	 * @returns An object containing the output key, type, and transformed value,
	 *   or `undefined` if no matching transformation was applied.
	 */
	applyTransformation(
		transformFunction: TransformFunction,
		inputValue: FunctionParamBase,
		outputValues:
			| {
					key: string;
					type: string;
					value: any;
			  }
			| undefined
	) {
		switch (transformFunction.type) {
			case TransformFunctionType.Trim: {
				outputValues = {
					key: `Trim ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value: (inputValue.value as string).trim(),
				};
				break;
			}
			case TransformFunctionType.TrimStart: {
				outputValues = {
					key: `Trim Start ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value: inputValue.value !== undefined ? (inputValue.value as string).trimStart() : '',
				};
				break;
			}
			case TransformFunctionType.TrimEnd: {
				outputValues = {
					key: `Trim End ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value: inputValue.value !== undefined ? (inputValue.value as string).trimEnd() : '',
				};
				break;
			}
			case TransformFunctionType.ToUpperCase: {
				outputValues = {
					key: `ToUpperCase ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined ? (inputValue.value as string).toLocaleUpperCase() : '',
				};
				break;
			}
			case TransformFunctionType.ToLowerCase: {
				outputValues = {
					key: `ToLowerCase ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined ? (inputValue.value as string).toLocaleLowerCase() : '',
				};
				break;
			}
			case TransformFunctionType.AddPrefix: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Add Prefix ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? `${inputValue.value2 as string}${inputValue.value as string}`
							: '',
				};
				break;
			}
			case TransformFunctionType.AddSuffix: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Add Suffix ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? `${inputValue.value as string}${inputValue.value2 as string}`
							: '',
				};
				break;
			}
			case TransformFunctionType.Combine: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Combine ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? `${inputValue.value as string}${inputValue.value2 as string}`
							: '',
				};
				break;
			}
			case TransformFunctionType.Substring: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}
				let outputValue = '';
				if (
					inputValue.value !== undefined &&
					inputValue.value2 !== undefined &&
					inputValue.value3 !== undefined
				) {
					let startIndex = 0;
					let endIndex: number | undefined;
					switch (inputValue.value2) {
						case SubstringStartIndexOptions.StaticValue: {
							startIndex = inputValue.value3 as number;
							break;
						}
						case SubstringStartIndexOptions.FirstIndexOf: {
							startIndex = (inputValue.value as string).indexOf(inputValue.value3 as string);
							break;
						}
						case SubstringStartIndexOptions.LastIndexOf: {
							startIndex = (inputValue.value as string).lastIndexOf(inputValue.value3 as string);
						}
					}
					if (inputValue.value4 !== undefined) {
						switch (inputValue.value4) {
							case SubstringEndIndexOptions.ToEndOfValue: {
								break;
							}
							case SubstringEndIndexOptions.StaticValue: {
								endIndex = inputValue.value5 as number;
								break;
							}
							case SubstringEndIndexOptions.FirstIndexOf: {
								endIndex = (inputValue.value as string).indexOf(inputValue.value5 as string);
								break;
							}
							case SubstringEndIndexOptions.LastIndexOf: {
								endIndex = (inputValue.value as string).lastIndexOf(inputValue.value5 as string);
							}
						}
					}
					if (endIndex && endIndex > -1) {
						outputValue = (inputValue.value as string).substring(startIndex, endIndex);
					} else {
						outputValue = (inputValue.value as string).substring(startIndex);
					}
				}

				outputValues = {
					key: `Substring ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value: outputValue,
				};
				break;
			}
			case TransformFunctionType.Replace: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				let stringInput: string;
				if (typeof inputValue.value === 'object') {
					stringInput = JSON.stringify(inputValue.value);
				} else {
					stringInput = inputValue.value.toString();
				}
				const replaceValue = inputValue.value2?.toString() ?? '';
				const replaceWith = inputValue.value3?.toString() ?? '';
				if (inputValue.key4 === ReplaceTypes.ReplaceFirstInstance) {
					inputValue.value = stringInput.replace(replaceValue, replaceWith);
				} else {
					inputValue.value = stringInput.replaceAll(replaceValue, replaceWith);
				}

				outputValues = {
					key: `Replace ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? inputValue.value
							: '',
				};
				break;
			}
			case TransformFunctionType.Add: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Addition ${transformFunction.index + 1} Output`,
					type: 'number',
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? (inputValue.value as number) + (inputValue.value2 as number)
							: '',
				};
				break;
			}
			case TransformFunctionType.Subtract: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Subtract ${transformFunction.index + 1} Output`,
					type: 'number',
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? (inputValue.value as number) - (inputValue.value2 as number)
							: '',
				};
				break;
			}
			case TransformFunctionType.Multiply: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Multiply ${transformFunction.index + 1} Output`,
					type: 'number',
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? (inputValue.value as number) * (inputValue.value2 as number)
							: '',
				};
				break;
			}
			case TransformFunctionType.Divide: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `Divide ${transformFunction.index + 1} Output`,
					type: 'number',
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? (inputValue.value as number) / (inputValue.value2 as number)
							: '',
				};
				break;
			}
			case TransformFunctionType.RoundUp: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `RoundUp ${transformFunction.index + 1} Output`,
					type: 'number',
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? this.roundUpOrDown(
									inputValue.value as number,
									inputValue.value2?.toString().length,
									'UP'
								)
							: '',
				};
				break;
			}
			case TransformFunctionType.RoundDown: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				outputValues = {
					key: `RoundDown ${transformFunction.index + 1} Output`,
					type: 'number',
					value:
						inputValue.value !== undefined && inputValue.value2 !== undefined
							? this.roundUpOrDown(
									inputValue.value as number,
									inputValue.value2?.toString().length,
									'DOWN'
								)
							: '',
				};
				break;
			}
			case TransformFunctionType.MathCeil: {
				outputValues = {
					key: `Math ceil ${transformFunction.index + 1} Output`,
					type: 'number',
					value: inputValue.value !== undefined ? Math.ceil(inputValue.value as number) : '',
				};
				break;
			}
			case TransformFunctionType.MathFloor: {
				outputValues = {
					key: `Math floor ${transformFunction.index + 1} Output`,
					type: 'number',
					value: inputValue.value !== undefined ? Math.floor(inputValue.value as number) : '',
				};
				break;
			}
			case TransformFunctionType.NumToString: {
				outputValues = {
					key: `NumToString ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value:
						inputValue.value !== undefined
							? JSON.stringify(JSON.stringify(inputValue.value as string))
							: '',
				};
				break;
			}
			case TransformFunctionType.StringToNum: {
				outputValues = {
					key: `StringToNum ${transformFunction.index + 1} Output`,
					type: 'number',
					value: inputValue.value !== undefined ? Number(inputValue.value as string) : '',
				};
				break;
			}
			case TransformFunctionType.AddField: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}
				if (transformFunction.selectedParams.value3 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value3 to be set.`
					);
				}

				(inputValue.value as any)[inputValue.value2 as string] = inputValue.value3;
				outputValues = {
					key: `AddField ${transformFunction.index + 1} Output`,
					type: 'object',
					value: inputValue.value !== undefined ? JSON.stringify(inputValue.value) : '',
				};
				break;
			}
			case TransformFunctionType.RemoveField: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}

				if (inputValue.key2 !== undefined) {
					delete (inputValue.value as any)[inputValue.key2 as string];
					outputValues = {
						key: `RemoveField ${transformFunction.index + 1} Output`,
						type: 'object',
						value: inputValue.value ? JSON.stringify(inputValue.value) : '',
					};
				}
				break;
			}
			case TransformFunctionType.ObjectToString: {
				outputValues = {
					key: `ObjectToString ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value: inputValue.value !== undefined ? JSON.stringify(inputValue.value) : '',
				};
				break;
			}
			case TransformFunctionType.StringToDatetime: {
				if (transformFunction.selectedParams.key2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires key2 to be set.`
					);
				}

				let output: DateTime<true> | DateTime<false>;
				if (inputValue.key2 === IfGateDateTimeFormatType.ISO) {
					output = DateTime.fromISO(inputValue.value as string, { zone: 'utc' }).toUTC();
				} else {
					output = DateTime.fromFormat(inputValue.value as string, inputValue.value2 as string, {
						zone: 'utc',
					}).toUTC();
				}
				outputValues = {
					key: `StringToDatetime ${transformFunction.index + 1} Output`,
					type: 'date',
					value: output,
				};
				break;
			}
			case TransformFunctionType.AddTime: {
				if (transformFunction.selectedParams.value2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires value2 to be set.`
					);
				}
				if (transformFunction.selectedParams.key2 === undefined) {
					throw new Error(
						`The ${TransformFunctionType[transformFunction.type]} function requires key2 to be set.`
					);
				}
				if (inputValue === undefined) {
					return;
				}

				if (
					inputValue.value !== undefined &&
					inputValue.value2 !== undefined &&
					inputValue.key2 !== undefined
				) {
					switch (inputValue.key2 as number) {
						case TransformDatetimeTypes.Second: {
							inputValue.value = (inputValue.value as Date).setSeconds(
								(inputValue.value as Date).getSeconds() + (inputValue.value2 as number)
							);
							break;
						}
						case TransformDatetimeTypes.Minute: {
							inputValue.value = (inputValue.value as Date).setMinutes(
								(inputValue.value as Date).getMinutes() + (inputValue.value2 as number)
							);
							break;
						}
						case TransformDatetimeTypes.Hour: {
							inputValue.value = (inputValue.value as Date).setHours(
								(inputValue.value as Date).getHours() + (inputValue.value2 as number)
							);
							break;
						}
						case TransformDatetimeTypes.Day: {
							inputValue.value = (inputValue.value as Date).setDate(
								(inputValue.value as Date).getDate() + (inputValue.value2 as number)
							);
							break;
						}
						case TransformDatetimeTypes.Month: {
							inputValue.value = (inputValue.value as Date).setMonth(
								(inputValue.value as Date).getMonth() + (inputValue.value2 as number)
							);
							break;
						}
						case TransformDatetimeTypes.Year: {
							inputValue.value = (inputValue.value as Date).setFullYear(
								(inputValue.value as Date).getFullYear() + (inputValue.value2 as number)
							);
							break;
						}
					}
					outputValues = {
						key: `AddTime ${transformFunction.index + 1} Output`,
						type: 'date',
						value: inputValue.value !== undefined ? new Date(inputValue.value as string) : '',
					};
				}
				break;
			}
			case TransformFunctionType.DatetimeToString: {
				outputValues = {
					key: `DateTimeToString ${transformFunction.index + 1} Output`,
					type: DataMapperMappingTypes.string,
					value: inputValue.value !== undefined ? JSON.stringify(inputValue.value as string) : '',
				};
				break;
			}
			case TransformFunctionType.StringToObject: {
				try {
					while (typeof inputValue.value === 'string') {
						inputValue.value = JSON.parse(inputValue.value);
					}
				} catch (error) {
					console.error(`Failed to parse string value to object.`);
				}
				outputValues = {
					key: `StringToObject ${transformFunction.index + 1} Output`,
					type: 'object',
					value: inputValue.value ? JSON.stringify(inputValue.value) : '',
				};
				break;
			}
		}

		return outputValues;
	}
}
